import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';

import { callCallableFunction, db } from '../firebaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useMortalityContext } from '../contexts/MortalityContext';

type TokenPackage = {
  id: string;
  name: string;
  tokens: number;
  priceLabel: string;
  description: string;
  bestValue?: boolean;
};

const TOKEN_PACKAGES: TokenPackage[] = [
  {
    id: 'starter',
    name: 'Starter Pack',
    tokens: 10,
    priceLabel: '$0 - beta bonus',
    description: 'Perfect for a handful of quick calculations.',
  },
  {
    id: 'pro',
    name: 'Advisor Pack',
    tokens: 25,
    priceLabel: '$0 - launch special',
    description: 'Recommended for active advisors.',
    bestValue: true,
  },
  {
    id: 'expert',
    name: 'Expert Pack',
    tokens: 50,
    priceLabel: '$0 - VIP access',
    description: 'Stock up so you never run out mid-case.',
  },
];

export function ProfilePage() {
  const { user, signOut } = useAuth();
  const { history } = useMortalityContext();
  const [tokens, setTokens] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<TokenPackage>(TOKEN_PACKAGES[1]);

  useEffect(() => {
    if (!user) {
      setTokens(null);
      return;
    }

    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(
      userRef,
      snapshot => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setTokens(typeof data.tokens === 'number' ? data.tokens : null);
        } else {
          setTokens(null);
        }
      },
      snapshotError => {
        console.error('Failed to load user tokens', snapshotError);
        setTokens(null);
      },
    );

    return () => unsubscribe();
  }, [user]);

  const handleAddTokens = async () => {
    if (!user) {
      return;
    }

    setProcessing(true);
    setMessage(null);
    setError(null);

    try {
      await callCallableFunction<{ packageId: string }, { tokens: number }>('purchaseTokenPackage', {
        packageId: selectedPackage.id,
      });
      setMessage(`Added ${selectedPackage.tokens} tokens to your account.`);
    } catch (purchaseError) {
      console.error('Failed to add tokens', purchaseError);
      setError(purchaseError instanceof Error ? purchaseError.message : 'Unable to add tokens right now.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <section className="panel">
      <div className="section-header">
        <div>
          <h1 className="section-title">Profile</h1>
          <p className="muted">Manage your account, usage, and beta token balance.</p>
        </div>
        <button className="button-secondary" type="button" onClick={() => { void signOut(); }}>
          Sign out
        </button>
      </div>

      <div className="profile-grid">
        <div className="profile-card">
          <h2>Account</h2>
          <p className="muted">Signed in as</p>
          <p className="profile-value">{user?.displayName ?? user?.email ?? 'Unknown user'}</p>
          <p className="muted">Email: {user?.email ?? '—'}</p>
        </div>

        <div className="profile-card">
          <h2>Usage</h2>
          <p className="muted">Saved calculations</p>
          <p className="profile-value">{history.length}</p>
        </div>

        <div className="profile-card">
          <h2>Tokens</h2>
          <p className="muted">Beta balance</p>
          <p className="profile-value">{tokens ?? '—'}</p>
        </div>
      </div>

      <div className="token-section">
        <h3>Need more tokens?</h3>
        <p className="muted">All packages are complimentary during beta.</p>
        <div className="token-options">
          {TOKEN_PACKAGES.map(pkg => (
            <label key={pkg.id} className={`token-card ${selectedPackage.id === pkg.id ? 'selected' : ''}`}>
              <input
                type="radio"
                name="tokenPackage"
                value={pkg.id}
                checked={selectedPackage.id === pkg.id}
                onChange={() => setSelectedPackage(pkg)}
              />
              <span>{pkg.name}</span>
              <strong>{pkg.tokens} tokens</strong>
              <span className="muted">{pkg.priceLabel}</span>
              <p>{pkg.description}</p>
            </label>
          ))}
        </div>
        {message ? <p className="auth-info">{message}</p> : null}
        {error ? <p className="auth-error">{error}</p> : null}
        <button className="button-primary" type="button" onClick={handleAddTokens} disabled={processing}>
          {processing ? 'Processing…' : 'Add tokens'}
        </button>
      </div>
    </section>
  );
}
